// @ts-nocheck
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { createFromProduct } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { BadgeTypeValues } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription from "@insite/content-library/Components/ProductDescription";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductCarouselProductStyles } from "@store/Overrides/Components/ProductCarouselProduct";
import ProductImage, { ProductImageStyles } from "@store/Overrides/Components/ProductImage";
import ProductPartNumbers from "@store/Overrides/Components/ProductPartNumbers";
import ProductPrice from "@store/Overrides/Components/ProductPrice";
import { productCardProps } from "@store/Themes/common/props/productCard";
import findSndParentProduct from "@store/Framework/Store/Store/Data/Products/Handlers/FindSndParentProduct";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import getMatchingDataFromDictionary from "@toolkit/Framework/Common/Utilities/getMatchingDataFromDictionary";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useProductsDataView } from "@toolkit/Framework/Store/Data/Products/ProductsHooks";
import React, { useContext, useEffect, useMemo } from "react";
import { connect, useDispatch } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: WidgetProps) => {
    const { userName } = state.context.session;
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    return {
        state,
        productInfoByProductId:
            state.components.productInfoLists.productInfoListById[ownProps.id]?.productInfoByProductId,
        isSignedIn: userName && !currentUserIsGuest,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ThemeProps<BaseTheme>;

export interface ScratchAndDentChildProductStyles {
    widgeTitle?: TypographyProps;
    productImageStyles?: ProductImageStyles;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
}

export const scratchAndDentChildProductStyles: ScratchAndDentChildProductStyles = {
    widgeTitle: {
        variant: "h2",
        as: "strong",
        color: "primary",
        css: css`
            display: block;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            width: 100%;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            overflow: hidden;
            width: 100%;
        `,
    },
    carouselSlide: {
        css: css`
            position: relative;
            flex: 0 0 auto;
            width: 100%;
            border: 1px solid ${getColor("common.border")};
            display: flex;
            > div {
                padding: ${pxToRem(15)};
            }
            > div:first-child {
                flex: 1;
                padding-right: 0;
            }
            > div:last-child {
                flex: 3;
            }
        `,
    },
    carouselSlideInner: productCardProps.card,
};

const styles = scratchAndDentChildProductStyles;
const ScratchAndDentChildProduct = ({ id, state, productInfoByProductId, isSignedIn }: Props) => {
    const dispatch = useDispatch();
    const { product } = useContext(ProductContext);
    const parameters = useMemo(() => {
        return { productIds: [product.properties.sndParentId], apiType: "snd", widgetId: id };
    }, [product.id]);
    const productsDataView = useProductsDataView(parameters);
    const products = productsDataView.value;
    const productInfos = getMatchingDataFromDictionary(products, productInfoByProductId);
    const trackViewItemList = ga.use.trackViewItemList(
        { items: products, item_list_name: "Get It Brand New", item_list_id: "sndCarousel" },
        { productInfos },
    );
    ga.use.productInfosCallback(productInfos, trackViewItemList, undefined, !isSignedIn);
    useEffect(() => {
        if (
            !productsDataView.value &&
            !productsDataView.isLoading &&
            parameters.productIds &&
            parameters.productIds.length > 0 &&
            parameters.productIds[0] !== undefined
        ) {
            dispatch(findSndParentProduct(parameters));
        }
    }, [parameters]);

    if (!product.properties.sndParentId && product.properties.isScratchAndDent !== "True") {
        return null;
    }

    if (!productsDataView || productsDataView?.value?.length === 0) {
        return null;
    }
    if (!products || !products.length) {
        return null;
    }
    return (
        <>
            <Typography {...styles.widgeTitle}>{translate("Get it brand new")}</Typography>
            <StyledWrapper>
                {products.map(product => {
                    const productContext = {
                        product,
                        productInfo: getProductInfoFromList(state, id, product.id) || createFromProduct(product),
                    };

                    const textBadges = product.badges?.filter(
                        o => o.displayOnBadgeWidget && o.badgeType === BadgeTypeValues.Text && o.displayText,
                    );

                    return (
                        <StyledWrapper {...styles.carouselSlide} id={`product_${product.id}`} key={product.id}>
                            <StyledWrapper>
                                <ProductImage
                                    product={productContext}
                                    extendedStyles={styles.productImageStyles}
                                    size="medium"
                                />
                            </StyledWrapper>
                            <StyledWrapper>
                                <ProductContext.Provider
                                    value={productContext}
                                    key={`${product.id}-snd-parent-inventory`}
                                >
                                    <ProductDescription product={productContext} />
                                    {textBadges && <ProductTextBadges badgeList={textBadges} maxBadges={4} />}
                                    <ProductPartNumbers
                                        productNumber={product.productNumber}
                                        customerProductNumber={product.customerProductNumber}
                                        manufacturerItem={product.manufacturerItem}
                                        showCustomerName={false}
                                        showManufacturerItem={false}
                                    />
                                    <ProductPrice showLabel={false} product={productContext} />
                                    <ProductContextAvailability />
                                </ProductContext.Provider>
                            </StyledWrapper>
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withTheme(ScratchAndDentChildProduct)),
    definition: {
        displayName: "Scratch And Dent Child Product Widget",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
